import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import { useRecoilState, useRecoilValue } from 'recoil';

import { lowerDisplayLineTestIDs, Theme } from '@constants';
import { useGetUserLocation } from '@hooks';
import { ldlPlaylistState, ldlShouldPlay } from '@store';

import { TWCText } from '../../primitives/TWCText/TWCText';
import { ldlPlaylist } from '../LDLBase/ldlPlaylist';
import { ldlProductMap } from '../LDLBase/ldlProductMap';
import { LDL } from '../styles';

/**
 * Play LDL Products with animation/transition
 * Severe Weather Crawl replaces all other LDL alerts
 */

const LDLProductPlayerAPI = () => {
  const { city: location } = useGetUserLocation();
  const [{ activeProductIndex, nextProductIndex }, setPlaylistState] = useRecoilState(ldlPlaylistState);
  const shouldPlay = useRecoilValue(ldlShouldPlay);
  const productName = ldlPlaylist[activeProductIndex];
  const Product = ldlProductMap[productName];

  return (
    <LDLProductPlayerUI
      activeProductIndex={activeProductIndex}
      location={location}
      shouldPlay={shouldPlay}
      onFadeOut={() =>
        setPlaylistState(oldState => ({
          ...oldState,
          activeProductIndex: nextProductIndex,
        }))
      }
    >
      <Product.API />
    </LDLProductPlayerUI>
  );
};

const LDLProductPlayerUI = ({ children, location, shouldPlay, onFadeOut }) => {
  const animatedOpacity = useRef(new Animated.Value(1)).current;
  const animatedTranslateY = useRef(new Animated.Value(0)).current;
  const slideAnimation = Animated.sequence([
    Animated.timing(animatedTranslateY, {
      duration: 50,
      toValue: LDL.height,
      useNativeDriver: true,
    }),
    Animated.timing(animatedOpacity, {
      duration: 50,
      toValue: 1,
      useNativeDriver: true,
    }),
    Animated.timing(animatedTranslateY, {
      duration: 250,
      toValue: 0,
      useNativeDriver: true,
    }),
  ]);

  useEffect(() => {
    shouldPlay && animate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPlay]);

  const fadeOut = () =>
    new Promise(resolve => {
      Animated.timing(animatedOpacity, {
        duration: 250,
        toValue: 0,
        useNativeDriver: true,
      }).start(resolve);
    });

  const animate = async () => {
    Animated.timing(animatedOpacity).reset();
    slideAnimation.reset();
    await fadeOut();
    onFadeOut?.();
    slideAnimation.start();
  };

  return (
    <View style={styles.container}>
      <TWCText style={styles.city} testID={lowerDisplayLineTestIDs.city} variant={'mediumCondensed'}>
        {location}
      </TWCText>
      <Animated.View
        style={[
          styles.animatedView,
          {
            opacity: animatedOpacity,
            transform: [{ translateY: animatedTranslateY }],
          },
        ]}
      >
        <View style={styles.productContainer}>{children}</View>
      </Animated.View>
    </View>
  );
};

export const LDLProductPlayer = {
  API: LDLProductPlayerAPI,
  UI: LDLProductPlayerUI,
};

const styles = StyleSheet.create({
  animatedView: {
    flexGrow: 1,
    height: LDL.height,
  },
  city: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingLeft: Theme.px(30),
    paddingRight: Theme.px(18),
  },
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    height: LDL.height,
    overflow: 'hidden',
    width: LDL.contentWidth,
  },
  productContainer: {
    alignItems: 'center',
    bottom: 0,
    flexDirection: 'row',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
});
